import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useCallback, useEffect, useMemo } from "react";
import {
  planningsByDepartmentAsync,
  tripsByDepartmentAsync,
  tripSlice,
} from "../../home/fleet/trips/tripsSlice";
import {
  selectAdvancedAnalyticsVehiclesList,
  selectAdvancedAnalyticsVehiclesListStatus,
} from "../../home/fleet/fleetSlice";
import { SharedInterfaceHistory } from "../../home/fleet/history/sharedInterface";
import {
  transactionsListOfADepartmentByPeriodAsync,
  authorizationsRejectedOfADepartmentByPeriodListAsync,
} from "../../home/transactions/transactionsSlice";
import { selectCurrentDepartment } from "../departments/departmentsSlice";
import { TripsAndPlanningsHistory } from "../../home/fleet/trips/tripsApi";
import { HistoryTripsSidePanel } from "../../home/fleet/history/sidePanel/sidePanel";

export const HistoryTab = () => {
  const params = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const firstPath = routerLocation.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const advancedAnalyticsVehicleList = useAppSelector(
    selectAdvancedAnalyticsVehiclesList,
  );
  const advancedAnalyticsVehicleListStatus = useAppSelector(
    selectAdvancedAnalyticsVehiclesListStatus,
  );
  const department = useAppSelector(selectCurrentDepartment);

  useEffect(() => {
    if (
      params.tab === "history" &&
      department &&
      !department.hasAdvancedAnalytics
    ) {
      navigate(`/${firstPath}`);
    }
  }, [params.tab, department, navigate, firstPath]);

  const loadTransactionByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!department) {
        return;
      }
      void dispatch(
        transactionsListOfADepartmentByPeriodAsync({
          departmentUuid: department.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [department, dispatch],
  );

  const loadAuthorizationsRejectedByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!department) {
        return;
      }
      void dispatch(
        authorizationsRejectedOfADepartmentByPeriodListAsync({
          departmentUuid: department.uuid,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [department, dispatch],
  );

  const canLoadTripsAndPlannings = useMemo(() => {
    return (
      department &&
      advancedAnalyticsVehicleList &&
      advancedAnalyticsVehicleList.length > 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    );
  }, [
    department,
    advancedAnalyticsVehicleList,
    advancedAnalyticsVehicleListStatus,
  ]);

  const loadPlanningsByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!department || !canLoadTripsAndPlannings) {
        return;
      }
      void dispatch(tripSlice.actions.resetPlanningsListStatus());
      void dispatch(
        planningsByDepartmentAsync({
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [department, canLoadTripsAndPlannings, dispatch],
  );

  const loadTripsByPeriodAction = useCallback(
    (payload: { from: string; to: string }) => {
      if (!department || !canLoadTripsAndPlannings) {
        return;
      }
      void dispatch(tripSlice.actions.resetTripsListStatus());
      void dispatch(
        tripsByDepartmentAsync({
          vehicles: advancedAnalyticsVehicleList,
          from: payload.from,
          to: payload.to,
        }),
      );
    },
    [
      department,
      canLoadTripsAndPlannings,
      dispatch,
      advancedAnalyticsVehicleList,
    ],
  );

  const hasNoAdvancedAnalyticsVehicles = useMemo(() => {
    return (
      advancedAnalyticsVehicleList.length === 0 &&
      advancedAnalyticsVehicleListStatus === "success"
    );
  }, [advancedAnalyticsVehicleList.length, advancedAnalyticsVehicleListStatus]);

  const sidePanelAction = useCallback(
    (props: { currentTripId: string }) => {
      navigate(
        `/${firstPath}/fleet/history/${props.currentTripId}${routerLocation.search}`,
      );
    },
    [navigate, firstPath, routerLocation.search],
  );

  if (params.tab !== "history") {
    return;
  }

  return (
    <SharedInterfaceHistory
      hasNoAdvancedAnalyticsVehicles={hasNoAdvancedAnalyticsVehicles}
      loadTransactionByPeriodAction={loadTransactionByPeriodAction}
      loadAuthorizationsRejectedByPeriodAction={
        loadAuthorizationsRejectedByPeriodAction
      }
      sidePanelAction={sidePanelAction}
      sidePanel={(props: {
        trip: TripsAndPlanningsHistory;
        previousTripId: string | null;
        nextTripId: string | null;
      }) => (
        <HistoryTripsSidePanel
          tripsAndPlanningsHistory={props.trip}
          previousTripId={props.previousTripId}
          nextTripId={props.nextTripId}
        />
      )}
      loadPlanningsByPeriodAction={loadPlanningsByPeriodAction}
      loadTripsByPeriodAction={loadTripsByPeriodAction}
    />
  );
};
